import { APP_ENVIRONMENTS, isAppEnvironment, type AppEnvironment } from '@solar/config';

/** Validated public configuration for the frontend. */
export interface WebConfig {
  readonly apiBaseUrl: string;
  readonly appName: string;
  readonly appEnv: AppEnvironment;
  readonly apiTimeoutMs: number;
}

const DEFAULT_API_BASE_URL = 'http://localhost:4000/api/v1';
const DEFAULT_APP_NAME = 'Solar Repayment Platform';
const DEFAULT_API_TIMEOUT_MS = 15_000;

/** Raised at startup when a `VITE_` variable is present but unusable. */
export class WebConfigError extends Error {
  readonly issues: readonly string[];

  constructor(issues: readonly string[]) {
    super(['Invalid frontend configuration:', ...issues.map((issue) => `  - ${issue}`)].join('\n'));
    this.name = 'WebConfigError';
    this.issues = issues;
  }
}

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

/**
 * Validates the public configuration.
 *
 * Runs at module load so a misconfigured deployment surfaces immediately,
 * rather than as a confusing network failure on the first request.
 */
export function readWebConfig(env: ImportMetaEnv): WebConfig {
  const issues: string[] = [];

  const rawBaseUrl = env.VITE_API_BASE_URL?.trim();
  let apiBaseUrl = DEFAULT_API_BASE_URL;

  if (rawBaseUrl !== undefined && rawBaseUrl !== '') {
    try {
      const parsed = new URL(rawBaseUrl);

      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        issues.push('VITE_API_BASE_URL must use http or https.');
      } else {
        apiBaseUrl = normalizeBaseUrl(rawBaseUrl);
      }
    } catch {
      issues.push(
        'VITE_API_BASE_URL must be an absolute URL, for example http://localhost:4000/api/v1.',
      );
    }
  }

  const rawAppEnv = env.VITE_APP_ENV?.trim();
  let appEnv: AppEnvironment = 'development';

  if (rawAppEnv !== undefined && rawAppEnv !== '') {
    if (isAppEnvironment(rawAppEnv)) {
      appEnv = rawAppEnv;
    } else {
      issues.push(`VITE_APP_ENV must be one of: ${APP_ENVIRONMENTS.join(', ')}.`);
    }
  }

  const rawTimeout = env.VITE_API_TIMEOUT_MS?.trim();
  let apiTimeoutMs = DEFAULT_API_TIMEOUT_MS;

  if (rawTimeout !== undefined && rawTimeout !== '') {
    const parsed = Number(rawTimeout);

    if (!Number.isInteger(parsed) || parsed <= 0) {
      issues.push('VITE_API_TIMEOUT_MS must be a positive whole number of milliseconds.');
    } else {
      apiTimeoutMs = parsed;
    }
  }

  if (issues.length > 0) {
    throw new WebConfigError(issues);
  }

  const appName = env.VITE_APP_NAME?.trim();

  return {
    apiBaseUrl,
    appName: appName !== undefined && appName !== '' ? appName : DEFAULT_APP_NAME,
    appEnv,
    apiTimeoutMs,
  };
}

export const webConfig: WebConfig = readWebConfig(import.meta.env);
